import { create } from "zustand";

import type {
  AlphaMatchMode,
  AlphaTutorialConcept,
  AlphaTutorialState,
} from "./alpha-tutorial-types";

interface AlphaTutorialStore extends AlphaTutorialState {
  resetTutorial: (mode: AlphaMatchMode) => void;
  nextWelcome: () => void;
  previousWelcome: () => void;
  skipWelcome: () => void;
  restartWelcome: () => void;
  setGuideOpen: (open: boolean) => void;
  dismissCurrentHint: () => void;
  completeConcepts: (concepts: readonly AlphaTutorialConcept[]) => void;
}

const initialState: AlphaTutorialState = {
  mode: "guided",
  enabled: true,
  completedConcepts: [],
  dismissedCurrentHint: false,
  // The welcome flow begins only after the player explicitly starts a Guided
  // match. This leaves deterministic test scenarios and Developer Mode alone.
  welcomeStep: null,
  guideOpen: false,
};

export const useAlphaTutorialStore = create<AlphaTutorialStore>((set) => ({
  ...initialState,
  resetTutorial: (mode) =>
    set({
      mode,
      enabled: mode === "guided",
      completedConcepts: [],
      dismissedCurrentHint: false,
      welcomeStep: mode === "guided" ? 0 : null,
      guideOpen: false,
    }),
  nextWelcome: () =>
    set((state) => ({
      welcomeStep: state.welcomeStep === 0 ? 1 : state.welcomeStep === 1 ? 2 : null,
    })),
  previousWelcome: () =>
    set((state) => ({
      welcomeStep: state.welcomeStep === 2 ? 1 : state.welcomeStep === 1 ? 0 : state.welcomeStep,
    })),
  skipWelcome: () => set({ welcomeStep: null, dismissedCurrentHint: false }),
  restartWelcome: () => set({ welcomeStep: 0, guideOpen: false, dismissedCurrentHint: false }),
  setGuideOpen: (guideOpen) => set({ guideOpen }),
  dismissCurrentHint: () => set({ dismissedCurrentHint: true }),
  completeConcepts: (concepts) =>
    set((state) => ({
      completedConcepts: [...new Set([...state.completedConcepts, ...concepts])],
      dismissedCurrentHint: false,
    })),
}));
